import React from 'react';
import { useRevenueDynamicsQuery } from '@/api';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { RevenueDynamics } from '@/api/models';
import { useSales } from '@/pages/sales/SalesProvider';

export type UseRevenueDynamicsResult = {
    data: RevenueDynamics | null;
    error: ErrorResponse | null;
    isLoading: boolean;
    onRetry: () => void;
    productId: string | null;
    setProductId: (id: string | null) => void;
};

/**
 * Drives the Revenue Dynamics widget. Reads the page-level date
 * range from `SalesProvider`, owns local product-filter state, and
 * threads both into `useRevenueDynamicsQuery`. The server pre-buckets
 * + pre-aggregates, so the View consumes the response unchanged.
 *
 * `productId` lives here (not in `SalesProvider`) because only this
 * widget cares about it.
 */
export const useRevenueDynamics = (): UseRevenueDynamicsResult => {
    const { dateRange } = useSales();
    const [productId, setProductId] = React.useState<string | null>(null);

    const query = useRevenueDynamicsQuery({
        from: dateRange.start,
        to: dateRange.end,
        productId,
    });
    const { refetch } = query;

    const data = React.useMemo<RevenueDynamics | null>(
        () => (query.data?.success ? query.data.response : null),
        [query.data],
    );

    const error = React.useMemo<ErrorResponse | null>(
        () =>
            query.data && !query.data.success
                ? (query.data.error ?? null)
                : null,
        [query.data],
    );

    const onRetry = React.useCallback(() => {
        void refetch();
    }, [refetch]);

    return {
        data,
        error,
        isLoading: query.isPending,
        onRetry,
        productId,
        setProductId,
    };
};
